/**
 * TTS Type Definitions
 * Shared types and interfaces for text-to-speech functionality
 * TODO - Add to public-core-base once feature CU-868jnybqdn is finalized and if it will be reused by the mobile app
 */

/**
 * Section types for organizing TTS content
 * @type {string}
 */
export type TTSSection = 'title' | 'header' | 'body' | 'local'

/**
 * Represents a single sentence with its section context and position
 * Used for tracking which sentence is currently being spoken
 *
 * @interface SectionedSentence
 * @property {TTSSection} section - Which content section this sentence belongs to
 * @property {number} index - The sentence index within that section
 * @property {string} text - The actual text of the sentence
 */
export interface SectionedSentence {
    section: TTSSection
    index: number
    text: string
}

/**
 * Callback fired when a sentence starts playing
 * Provides section context for highlighting coordination
 *
 * @callback OnSentenceChange
 * @param {object | null} sentenceInfo - Current sentence info or null if playback ended
 * @param {TTSSection} sentenceInfo.section - Which content section this sentence belongs to
 * @param {number} sentenceInfo.index - The sentence index within that section
 */
export type OnSentenceChange = (sentenceInfo: { section: TTSSection; index: number } | null) => void

/**
 * TTS playback state
 *
 * @interface TTSState
 * @property {boolean} isPlaying - Whether audio is currently playing
 * @property {boolean} isPaused - Whether playback is paused
 * @property {number} currentSentence - Index of the currently highlighted sentence
 * @property {string[] | null} voices - Available voices for the current language
 * @property {string | null} selectedVoice - Currently selected voice
 */
export interface TTSState {
    isPlaying: boolean
    isPaused: boolean
    currentSentence: number
    voices: SpeechSynthesisVoice[] | null
    selectedVoice: SpeechSynthesisVoice | null
}

/**
 * Text content organized by section for TTS processing
 *
 * @interface TTSText
 * @property {string} [title] - Document/content title
 * @property {string} [header] - Header/subtitle text
 * @property {string} [body] - Main body content
 * @property {string[]} [local] - Local/supplementary content blocks (e.g., community sponsor info)
 */
export interface TTSText {
    title?: string
    header?: string
    body?: string
    local?: string[]
}

/**
 * Context for highlighting calculations during TTS playback
 * Used to map sentence indices to character ranges in rendered content
 *
 * @interface HighlightContext
 * @property {number} [highlightedSentenceIndex] - Index of the sentence to highlight
 * @property {Array<{start: number; end: number}>} [sentenceRanges] - Character ranges for each sentence
 * @property {string} [fullText] - Full text content for position calculations
 */
export interface HighlightContext {
    highlightedSentenceIndex?: number
    sentenceRanges?: Array<{ start: number; end: number }>
    fullText?: string
}

/**
 * Error type for TTS operations
 */
export enum TTSErrorType {
    NOT_SUPPORTED = 'NOT_SUPPORTED',
    VOICE_NOT_FOUND = 'VOICE_NOT_FOUND',
    UTTERANCE_ERROR = 'UTTERANCE_ERROR',
    UNKNOWN = 'UNKNOWN',
}

/**
 * Custom error class for TTS operations
 */
export class TTSError extends Error {
    constructor(
        public type: TTSErrorType,
        message: string,
        public originalError?: Error
    ) {
        super(message)
        this.name = 'TTSError'
    }
}

/**
 * Logger utility for TTS operations
 * Helps with debugging TTS-related issues
 */
export const TTSLogger = {
    info: (message: string, data?: unknown) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(`[TTS] ${message}`, data ?? '')
        }
    },

    warn: (message: string, error?: Error) => {
        console.warn(`[TTS Warning] ${message}`, error ?? '')
    },

    error: (message: string, error: Error | TTSError) => {
        if (error instanceof TTSError) {
            console.error(
                `[TTS Error] ${message} - Type: ${error.type}`,
                error.originalError ?? error
            )
        } else {
            console.error(`[TTS Error] ${message}`, error)
        }
    },
}
